import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { ListItem } from "@sortera/react-components";
import { CheckIcon, ChevronUpDownIcon, LayoutGridIcon } from "@sortera/react-icons";
import type { ProjectDto } from "../../types/index.ts";
import styles from "./project-switcher.module.css";

export interface ProjectSwitcherProps {
  projects: ProjectDto[] | null;
  repo: string | null;
  onSelect: (repo: string) => void;
}

/**
 * Which project the conversations are about: the current one as a row you can click, and the
 * others in a list that opens under it — a switcher, not a form field.
 *
 * Built here because the design system has rows (ListItem) but no menu to put them in; its
 * SelectField is a labelled form control, which is heavier than a sidebar wants.
 */
export function ProjectSwitcher({ projects, repo, onSelect }: ProjectSwitcherProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const current = projects?.find((p) => p.repo === repo) ?? null;
  const usable = Boolean(projects?.length);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    // Focus the chosen project, or the first, so the keyboard starts where the eye does.
    const items = root.current?.querySelectorAll<HTMLButtonElement>("[data-project]");
    const chosen = Array.from(items ?? []).find((el) => el.dataset.project === repo) ?? items?.[0];
    chosen?.focus();
    return () => window.removeEventListener("pointerdown", onDown);
  }, [open, repo]);

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(root.current?.querySelectorAll<HTMLButtonElement>("[data-project]") ?? []);
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const label = current?.name ?? (projects ? (usable ? "Välj projekt" : "Inget projekt ännu") : "Hämtar projekt …");

  return (
    <div className={styles.root} ref={root}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={!usable}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && usable) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={styles.tile} aria-hidden>
          <LayoutGridIcon size={16} />
        </span>
        <span className={styles.names}>
          <span className={styles.label}>Projekt</span>
          <span className={styles.name} data-placeholder={!current}>
            {label}
          </span>
        </span>
        <span className={styles.chevron} aria-hidden>
          <ChevronUpDownIcon size={16} />
        </span>
      </button>

      {open && projects && (
        <div className={styles.menu} id={menuId} role="group" aria-label="Byt projekt" onKeyDown={onMenuKey}>
          {projects.map((p) => (
            <ListItem
              key={p.repo}
              size="desktop"
              data-project={p.repo}
              aria-current={p.repo === repo || undefined}
              icon={<LayoutGridIcon />}
              trailingIcon={p.repo === repo ? <CheckIcon /> : undefined}
              onClick={() => {
                setOpen(false);
                if (p.repo !== repo) onSelect(p.repo);
                trigger.current?.focus();
              }}
            >
              {p.name}
            </ListItem>
          ))}
        </div>
      )}
    </div>
  );
}
